import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ClipCategory, ClipLabelEdit, ClipLabelVocabulary, LibraryClipLabel } from 'services/VideoEditingService';
import { Draft, clipLabelEditFrom, draftFrom } from 'pages/VideoEditing/components/ClipLabelFields';
import ClipLabelSummary from 'pages/VideoEditing/components/ClipLabelSummary';
import ClipLabelsDialog from 'pages/VideoEditing/components/ClipLabelsDialog';
import ClipTrimmer, { ClipTrimmerRange } from 'pages/VideoEditing/components/ClipTrimmer';
import SaveAsNewClipDialog from './SaveAsNewClipDialog';

export interface ClipEditSave {
  edit: ClipLabelEdit;
  // True when the range moved, so the caller knows the poster was regenerated and a plain
  // in-place patch of the row won't pick up the new image.
  rangeMoved: boolean;
}

interface ClipEditDialogProps {
  label: LibraryClipLabel;
  vocabulary: ClipLabelVocabulary | null;
  categories: ClipCategory[] | null;
  onCreateCategory: (name: string) => Promise<ClipCategory>;
  onClose: () => void;
  onSave: (labelId: string, save: ClipEditSave) => Promise<void>;
  onSaveAsNew: (source: LibraryClipLabel, range: ClipTrimmerRange, edit: ClipLabelEdit) => Promise<void>;
}

/**
 * Edit one library clip: what it says AND what it covers, in one place, saved once.
 *
 * This replaces a pair of dialogs — a read-only "Edit" panel whose fields hid behind a pencil,
 * and a separate "Trim" dialog — which between them meant changing a clip's name and its range
 * was two trips through two modals, both writing to the same endpoint.
 *
 * The labels show as a static summary with one "Edit details" button opening its own modal. They
 * were briefly always-open fields, which fixed the hidden-pencil problem but made this dialog
 * taller than a screen; the nested modal keeps both properties.
 *
 * Every clip gets the same modal: a whole take is retrimmable like any other, so there is no
 * scope-dependent branch here.
 */
export default function ClipEditDialog({
  label, vocabulary, categories, onCreateCategory, onClose, onSave, onSaveAsNew,
}: ClipEditDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(label));
  const [range, setRange] = useState<ClipTrimmerRange & { changed: boolean; valid: boolean }>({
    inSec: label.inSec ?? 0,
    outSec: label.outSec ?? label.durationSec ?? 0,
    changed: false,
    valid: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingAsNew, setSavingAsNew] = useState(false);
  const [editingLabels, setEditingLabels] = useState(false);
  // The model's confidence in its product guess stops being offered or shown once a person has
  // saved the label.
  const humanLabeled = label.labelSource === 'human';

  // Re-seed when a different clip is opened in the same mounted dialog.
  useEffect(() => { setDraft(draftFrom(label)); setError(null); }, [label.labelId]);

  const handleRangeChange = useCallback(
    (next: ClipTrimmerRange & { changed: boolean; valid: boolean }) => setRange(next),
    [],
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const edit = clipLabelEditFrom(draft, { categoriesOffered: !!categories });
      // Only send the range when it actually moved. Sending an unchanged range would still
      // count as a change server-side and needlessly re-derive the poster and sound ratio.
      const rangeMoved = range.changed && range.valid;
      await onSave(label.labelId, {
        edit: rangeMoved ? { ...edit, inSec: range.inSec, outSec: range.outSec } : edit,
        rangeMoved,
      });
      onClose();
    } catch (err: any) {
      // Inline, not a page-level snackbar: the person is looking at this form.
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {label.name || label.fileName || 'Edit clip'}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label.planName || 'no project'}
            {' · drag the handles to change what this clip covers'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <ClipTrimmer
            fileId={label.fileId}
            streamUrl={label.streamUrl}
            // Includes the range: it's the "different clip loaded" boundary, so without it
            // re-opening after a trim would keep the previous selection.
            resetKey={`${label.labelId}:${label.inSec ?? ''}:${label.outSec ?? ''}`}
            knownDuration={label.durationSec}
            initialInSec={label.inSec ?? 0}
            initialOutSec={label.outSec ?? label.durationSec}
            // Range reported to us rather than rendered as buttons inside the trimmer: Save
            // belongs in the dialog footer, and `actions` doesn't render until the video's
            // duration loads.
            onRangeChange={handleRangeChange}
          />
          {/* Static, with the editor in its own modal. Always-open fields under the trimmer
              made this dialog taller than a screen; an inline pencil is what made pressing
              "Edit" produce a read-only summary in the first place. */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <ClipLabelSummary
                draft={draft}
                vocabulary={vocabulary}
                categories={categories}
                humanLabeled={humanLabeled}
              />
            </Box>
            <Button
              size="small"
              startIcon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => setEditingLabels(true)}
              disabled={saving}
            >
              Edit details
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<ContentCutIcon fontSize="small" />}
            disabled={saving || !range.valid}
            onClick={() => setSavingAsNew(true)}
          >
            Save as new clip…
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {editingLabels && (
        <ClipLabelsDialog
          draft={draft}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          showConfidence={!humanLabeled}
          onDone={(next) => { setDraft(next); setEditingLabels(false); }}
          onCancel={() => setEditingLabels(false)}
        />
      )}

      {savingAsNew && (
        <SaveAsNewClipDialog
          source={label}
          range={{ inSec: range.inSec, outSec: range.outSec }}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          onClose={() => setSavingAsNew(false)}
          onSave={async (edit) => {
            await onSaveAsNew(label, { inSec: range.inSec, outSec: range.outSec }, edit);
            setSavingAsNew(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
